import React, { Fragment, useState, useEffect } from "react";
import { closeModal } from "../../store/modal/modalReducer";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, Transition } from "@headlessui/react";
import ErrorBoundary from "../../containers/ErrorBoundary";
import NotificationToast from "../Helpers/NotificationToast";
import { faTimes, faSpinner, faCalendarPlus, faBell, faBellSlash, faExclamationTriangle } from "@fortawesome/pro-regular-svg-icons";
import { format, isToday } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function ModalManualAppointment(props) {
    const dispatch = useDispatch();
    const [open] = useState(true);
    const { current_booking } = useSelector((state) => state.booking);

    const [appointmentTypes, setAppointmentTypes] = useState([]);
    const [selectedType, setSelectedType] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [timeSlots, setTimeSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [description, setDescription] = useState("");
    const [sendNotification, setSendNotification] = useState(true);
    const [loading, setLoading] = useState(false);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [isOutsideAvailability, setIsOutsideAvailability] = useState(false);
    const [isSameDay, setIsSameDay] = useState(false);
    const [fullyBooked, setFullyBooked] = useState(false);

    // Asana fields
    const [assistanceTypes, setAssistanceTypes] = useState([]);
    const [maintenanceOptions, setMaintenanceOptions] = useState([]);
    const [selectedAssistanceType, setSelectedAssistanceType] = useState("");
    const [selectedAssistanceTypeGid, setSelectedAssistanceTypeGid] = useState("");
    const [selectedMaintenanceOption, setSelectedMaintenanceOption] = useState("");
    const [selectedMaintenanceOptionGid, setSelectedMaintenanceOptionGid] = useState("");
    const [maintenanceDescription, setMaintenanceDescription] = useState("");
    const [loadingAsanaFields, setLoadingAsanaFields] = useState(false);

    useEffect(() => {
        if (current_booking?.property_id) {
            setLoading(true);
            axios({
                method: "get",
                url: `${process.env.REACT_APP_API_URL}appointment_types_for_property`,
                params: { property_id: current_booking.property_id },
            })
                .then((response) => {
                    setAppointmentTypes(response.data);
                })
                .catch((err) => {
                    console.error("Failed to load appointment types:", err);
                    toast(
                        <NotificationToast title="Error" message="Failed to load appointment types" type="error" />,
                        { autoClose: false }
                    );
                })
                .finally(() => setLoading(false));
        }
    }, [current_booking?.property_id]);

    const handleTypeChange = (e) => {
        const typeId = parseInt(e.target.value);
        const type = appointmentTypes.find((t) => t.id === typeId);
        setSelectedType(type || null);
        setSelectedDate(null);
        setTimeSlots([]);
        setSelectedSlot(null);
        setIsOutsideAvailability(false);
        setIsSameDay(false);
        setFullyBooked(false);

        // Load Asana fields if this appointment type has send_to_asana enabled
        if (type && type.send_to_asana == 1) {
            loadAsanaFields();
        } else {
            // Clear Asana fields
            setAssistanceTypes([]);
            setMaintenanceOptions([]);
            setSelectedAssistanceType("");
            setSelectedAssistanceTypeGid("");
            setSelectedMaintenanceOption("");
            setSelectedMaintenanceOptionGid("");
            setMaintenanceDescription("");
        }
    };

    const loadAsanaFields = async () => {
        setLoadingAsanaFields(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}asana_field_options`);
            
            if (response.data.assistance_types) {
                setAssistanceTypes(response.data.assistance_types);
            }
            
            if (response.data.maintenance_options) {
                setMaintenanceOptions(response.data.maintenance_options);
            }
        } catch (error) {
            console.error('Failed to load Asana fields:', error);
            toast(
                <NotificationToast title="Warning" message="Failed to load Asana fields" type="warning" />
            );
        } finally {
            setLoadingAsanaFields(false);
        }
    };

    const generateDefaultSlots = (forDate) => {
        // Generate 30-minute interval slots from 08:00 to 17:00
        const slots = [];
        const duration = selectedType?.duration_minutes || 30;

        // For today, only offer times that haven't already passed.
        const sameDay = forDate && isToday(forDate);
        const nowMinutes = new Date().getHours() * 60 + new Date().getMinutes();

        for (let hour = 8; hour < 17; hour++) {
            for (let minute = 0; minute < 60; minute += 30) {
                const startMinutes = hour * 60 + minute;
                const endMinutes = startMinutes + duration;
                const endHour = Math.floor(endMinutes / 60);
                const endMin = endMinutes % 60;

                // Skip past times when booking for today
                if (sameDay && startMinutes <= nowMinutes) {
                    continue;
                }

                // Don't create slots that end after 17:00
                if (endHour < 17 || (endHour === 17 && endMin === 0)) {
                    const from = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
                    const to = `${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`;
                    slots.push({ from, to, is_full_day: false });
                }
            }
        }
        return slots;
    };

    const handleDateChange = async (date) => {
        setSelectedDate(date);
        setSelectedSlot(null);
        setTimeSlots([]);
        setIsOutsideAvailability(false);
        setIsSameDay(false);
        setFullyBooked(false);

        if (!selectedType || !date) return;

        const sameDay = isToday(date);
        setIsSameDay(sameDay);

        setLoadingSlots(true);

        try {
            const requestData = {
                appointment_id: selectedType.id,
                date: format(date, "yyyy-MM-dd"),
                property_id: current_booking.property_id,
                // Staff manual load may book same-day; ask the backend for real
                // availability instead of the blocked empty response tenants get.
                allow_same_day: true,
            };

            if (current_booking?.unit_id) {
                requestData.unit_id = current_booking.unit_id;
            }

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}manual_check_availability`,
                requestData
            );

            const slots = response.data?.slots || [];
            const hasSchedule = response.data?.has_schedule;

            if (slots.length > 0) {
                // Real availability - only the genuinely open slots (booked/past removed).
                setTimeSlots(slots);
                setIsOutsideAvailability(false);
                setFullyBooked(false);

                // Auto-select full day slots
                const fullDaySlot = slots.find((s) => s.is_full_day);
                if (fullDaySlot) {
                    setSelectedSlot(fullDaySlot);
                }
            } else if (hasSchedule) {
                // A roster exists but nothing is open -> fully booked. Do NOT fabricate
                // slots, so the agent can't accidentally double-book a taken time.
                setTimeSlots([]);
                setIsOutsideAvailability(false);
                setFullyBooked(true);
            } else {
                // No roster for this date -> outside normal availability. Allow an
                // override booking against default working-hours slots, with a warning.
                setTimeSlots(generateDefaultSlots(date));
                setIsOutsideAvailability(true);
                setFullyBooked(false);
            }
        } catch (error) {
            console.error("Error fetching slots:", error);
            // On error, generate default slots
            setTimeSlots(generateDefaultSlots(date));
            setIsOutsideAvailability(true);
        } finally {
            setLoadingSlots(false);
        }
    };

    const getSelectedTime = () => {
        if (selectedSlot) {
            return { from: selectedSlot.from, to: selectedSlot.to };
        }
        return null;
    };

    const handleSubmit = async () => {
        const time = getSelectedTime();

        if (!selectedType) {
            toast(<NotificationToast title="Error" message="Please select an appointment type" type="error" />);
            return;
        }
        if (!selectedDate) {
            toast(<NotificationToast title="Error" message="Please select a date" type="error" />);
            return;
        }
        if (!time || !time.from || !time.to) {
            toast(<NotificationToast title="Error" message="Please select or enter a time" type="error" />);
            return;
        }

        setSubmitting(true);

        try {
            const payload = {
                booking_id: current_booking.id,
                appointment_id: selectedType.id,
                booked_date: format(selectedDate, "yyyy-MM-dd"),
                booked_time: time.from.length === 5 ? time.from + ":00" : time.from,
                time_to: time.to.length === 5 ? time.to + ":00" : time.to,
                description: description,
                send_notification: sendNotification,
            };

            // Add Asana fields if appointment type has send_to_asana enabled
            if (selectedType.send_to_asana == 1) {
                payload.assistance_type = selectedAssistanceType;
                payload.assistance_type_gid = selectedAssistanceTypeGid;
                payload.maintenance_option = selectedMaintenanceOption;
                payload.maintenance_option_gid = selectedMaintenanceOptionGid;
                payload.maintenance_description = maintenanceDescription;
            }

            const response = await axios.post(`${process.env.REACT_APP_API_URL}manual_create_appointment`, payload);

            toast(
                <NotificationToast
                    title="Appointment Loaded"
                    message={
                        response.data.success +
                        (response.data.conflicts > 0 ? ` (${response.data.conflicts} other booking(s) in this slot)` : "")
                    }
                />
            );

            dispatch(closeModal());

            if (props.onSuccess) {
                props.onSuccess();
            }
        } catch (error) {
            const errorMsg = error.response?.data?.error || "Failed to create appointment";
            toast(<NotificationToast title="Error" message={errorMsg} type="error" />, { autoClose: false });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ErrorBoundary>
            <Transition appear show={open} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => dispatch(closeModal())}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-lg transform overflow-visible rounded-2xl bg-white shadow-xl transition-all">
                                    {/* Header */}
                                    <div className="flex items-center justify-between bg-dark_background rounded-t-2xl px-6 py-4">
                                        <div className="flex items-center space-x-3">
                                            <FontAwesomeIcon icon={faCalendarPlus} className="h-5 w-5 text-primary" />
                                            <Dialog.Title className="text-lg font-light text-white">
                                                Load Appointment
                                            </Dialog.Title>
                                        </div>
                                        <button onClick={() => dispatch(closeModal())} className="text-white hover:text-gray-300">
                                            <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
                                        </button>
                                    </div>

                                    <div className="px-6 py-5 space-y-5">
                                        {/* Booking Info */}
                                        <div className="bg-gray-50 rounded-lg p-3 text-sm">
                                            <p className="font-medium">{current_booking?.user?.name} {current_booking?.user?.surname}</p>
                                            <p className="text-gray-500">Ref: {current_booking?.booking_reference}</p>
                                        </div>

                                        {/* Appointment Type */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Appointment Type</label>
                                            {loading ? (
                                                <div className="flex items-center space-x-2 text-gray-400">
                                                    <FontAwesomeIcon icon={faSpinner} spin />
                                                    <span>Loading types...</span>
                                                </div>
                                            ) : (
                                                <select
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                                                    value={selectedType?.id || ""}
                                                    onChange={handleTypeChange}
                                                >
                                                    <option value="">Select appointment type...</option>
                                                    {appointmentTypes.map((type) => (
                                                        <option key={type.id} value={type.id}>
                                                            {type.name} ({type.duration_minutes} min)
                                                        </option>
                                                    ))}
                                                </select>
                                            )}
                                        </div>

                                        {/* Date Picker */}
                                        {selectedType && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                                                <DatePicker
                                                    selected={selectedDate}
                                                    onChange={handleDateChange}
                                                    minDate={new Date()}
                                                    dateFormat="EEE, dd MMM yyyy"
                                                    placeholderText="Select a date..."
                                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                                                />
                                            </div>
                                        )}

                                        {/* Time Slots */}
                                        {selectedDate && selectedType && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Time Slot</label>

                                                {loadingSlots ? (
                                                    <div className="flex items-center space-x-2 text-gray-400 py-2">
                                                        <FontAwesomeIcon icon={faSpinner} spin />
                                                        <span>Checking availability...</span>
                                                    </div>
                                                ) : (
                                                    <>
                                                        {isOutsideAvailability && (
                                                            <div className="flex items-start space-x-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                                                                <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4 text-amber-500 mt-0.5" />
                                                                <span className="text-sm text-amber-700">
                                                                    <strong>Warning:</strong> This date is not officially available for appointments. Booking outside normal availability hours.
                                                                </span>
                                                            </div>
                                                        )}

                                                        {isSameDay && !isOutsideAvailability && !fullyBooked && timeSlots.length > 0 && (
                                                            <div className="flex items-start space-x-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                                                                <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4 text-amber-500 mt-0.5" />
                                                                <span className="text-sm text-amber-700">
                                                                    <strong>Warning:</strong> You are booking for today. Times shown exclude slots that are already booked &mdash; please double-check before confirming.
                                                                </span>
                                                            </div>
                                                        )}

                                                        {fullyBooked && (
                                                            <div className="flex items-start space-x-2 bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                                                                <FontAwesomeIcon icon={faExclamationTriangle} className="h-4 w-4 text-red-500 mt-0.5" />
                                                                <span className="text-sm text-red-700">
                                                                    <strong>Fully booked:</strong> There are no available time slots for this date &mdash; every rostered slot is already taken.
                                                                </span>
                                                            </div>
                                                        )}

                                                        {timeSlots.length > 0 && !timeSlots[0]?.is_full_day && (
                                                            <div className="grid grid-cols-4 gap-2 max-h-40 overflow-y-auto mb-3">
                                                                {timeSlots.map((slot, idx) => (
                                                                    <button
                                                                        key={idx}
                                                                        onClick={() => setSelectedSlot(slot)}
                                                                        className={`text-xs px-2 py-2 rounded-lg border transition-colors ${
                                                                            selectedSlot === slot
                                                                                ? "bg-primary text-white border-primary"
                                                                                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                                                        }`}
                                                                    >
                                                                        {slot.from}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {timeSlots[0]?.is_full_day && (
                                                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3 text-sm text-green-700">
                                                                Full day appointment selected (00:00 - 23:59)
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {/* Description */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                                            <textarea
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                placeholder="Notes about this appointment..."
                                                rows={2}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                                            />
                                        </div>

                                        {/* Asana Fields - Only show if appointment type has send_to_asana enabled */}
                                        {selectedType && selectedType.send_to_asana == 1 && (
                                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                                                <h3 className="text-sm font-semibold text-blue-900 mb-3">Asana Maintenance Details</h3>
                                                
                                                {loadingAsanaFields ? (
                                                    <div className="flex items-center space-x-2 text-gray-400 py-2">
                                                        <FontAwesomeIcon icon={faSpinner} spin />
                                                        <span>Loading Asana fields...</span>
                                                    </div>
                                                ) : (
                                                    <>
                                                        {/* Assistance Type */}
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Type of Assistance</label>
                                                            <select
                                                                value={selectedAssistanceTypeGid}
                                                                onChange={(e) => {
                                                                    const selectedOption = assistanceTypes.find(t => t.gid === e.target.value);
                                                                    setSelectedAssistanceTypeGid(e.target.value);
                                                                    setSelectedAssistanceType(selectedOption?.name || '');
                                                                }}
                                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                                                            >
                                                                <option value="">Select type...</option>
                                                                {assistanceTypes.map((type) => (
                                                                    <option key={type.gid} value={type.gid}>
                                                                        {type.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        {/* Maintenance Option */}
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Maintenance Option</label>
                                                            <select
                                                                value={selectedMaintenanceOptionGid}
                                                                onChange={(e) => {
                                                                    const selectedOption = maintenanceOptions.find(o => o.gid === e.target.value);
                                                                    setSelectedMaintenanceOptionGid(e.target.value);
                                                                    setSelectedMaintenanceOption(selectedOption?.name || '');
                                                                }}
                                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                                                            >
                                                                <option value="">Select option...</option>
                                                                {maintenanceOptions.map((option) => (
                                                                    <option key={option.gid} value={option.gid}>
                                                                        {option.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        {/* Maintenance Description */}
                                                        <div>
                                                            <label className="block text-sm font-medium text-gray-700 mb-1">Additional Details</label>
                                                            <textarea
                                                                value={maintenanceDescription}
                                                                onChange={(e) => setMaintenanceDescription(e.target.value)}
                                                                placeholder="Extra details about the maintenance request..."
                                                                rows={2}
                                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                                                            />
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {/* Send Notification Toggle */}
                                        <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                                            <div className="flex items-center space-x-3">
                                                <FontAwesomeIcon
                                                    icon={sendNotification ? faBell : faBellSlash}
                                                    className={`h-5 w-5 ${sendNotification ? "text-green-500" : "text-gray-400"}`}
                                                />
                                                <div>
                                                    <p className="text-sm font-medium text-gray-700">
                                                        {sendNotification ? "Send Notification" : "Silent Load"}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {sendNotification
                                                            ? "Tenant will receive appointment approved notification"
                                                            : "No notification will be sent to the tenant"}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => setSendNotification(!sendNotification)}
                                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                                    sendNotification ? "bg-green-500" : "bg-gray-300"
                                                }`}
                                            >
                                                <span
                                                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                                        sendNotification ? "translate-x-6" : "translate-x-1"
                                                    }`}
                                                />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="flex justify-end space-x-3 px-6 py-4 border-t border-gray-200">
                                        <button
                                            onClick={() => dispatch(closeModal())}
                                            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleSubmit}
                                            disabled={submitting || !selectedType || !selectedDate || !selectedSlot}
                                            className={`px-4 py-2 text-sm text-white rounded-lg transition-colors flex items-center space-x-2 ${
                                                submitting || !selectedType || !selectedDate || !selectedSlot
                                                    ? "bg-gray-300 cursor-not-allowed"
                                                    : "bg-primary hover:bg-primary/90"
                                            }`}
                                        >
                                            {submitting && <FontAwesomeIcon icon={faSpinner} spin />}
                                            <span>{submitting ? "Loading..." : "Load Appointment"}</span>
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </ErrorBoundary>
    );
}
